import React, { useState } from "react";
import { User, LogOut, Mail, Shield } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";

interface UserProfileDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserProfileDialog: React.FC<UserProfileDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { user, handleLogout } = useAuth();
  const [profileImageError, setProfileImageError] = useState(false);

  if (!isOpen) return null;

  const handleLogoutClick = async () => {
    await handleLogout();
    onClose();
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap: { [key: string]: string } = {
      admin: "Administrator",
      teacher: "Teacher",
      student: "Student",
      "branch-admin": "Branch Admin",
      "super-admin": "Super Admin",
    };
    return roleMap[role] || role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/5" onClick={onClose} />

      {/* Dialog */}
      <div className="fixed top-16 right-4 z-50 bg-white/95 backdrop-blur-xl rounded-xl shadow-xl border border-slate-200/60 w-80 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 p-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden shadow-md ${
                !user?.profileDetails?.profilePicture || profileImageError
                  ? "bg-gradient-to-br from-blue-300 to-indigo-400"
                  : ""
              }`}
            >
              {user?.profileDetails?.profilePicture && !profileImageError ? (
                <img
                  src={user.profileDetails.profilePicture}
                  alt={`${user.firstName || "User"} ${
                    user.lastName || ""
                  }`.trim()}
                  className="w-full h-full object-cover"
                  onError={() => setProfileImageError(true)}
                />
              ) : (
                <User className="w-6 h-6 text-white" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-900">
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.email?.split("@")[0] || "User"}
              </h3>
              <p className="text-sm font-semibold text-slate-600 capitalize mt-1">
                {user?.roles?.[0] ? getRoleDisplayName(user.roles[0]) : "User"}
              </p>
            </div>
          </div>
        </div>

        {/* User Details */}
        <div className="p-4 space-y-1">
          <div className="flex items-center gap-2 p-2.5 bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg">
            <div className="p-1.5 bg-white rounded-lg shadow-sm">
              <Mail className="w-4 h-4 text-blue-500" />
            </div>
            <span className="text-sm font-semibold text-slate-700 break-all">
              {user?.email}
            </span>
          </div>

          {user?.roles && user.roles.length > 0 && (
            <div className="flex items-start gap-2 p-2.5 bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg">
              <div className="p-1.5 bg-white rounded-lg shadow-sm">
                <Shield className="w-4 h-4 text-purple-500" />
              </div>
              <div className="flex flex-wrap gap-1 flex-1">
                {user.roles.map((role, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gradient-to-r from-blue-300 to-indigo-400 text-white rounded-xl text-xs font-bold capitalize shadow-md"
                  >
                    {getRoleDisplayName(role)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50 p-4">
          <button
            onClick={handleLogoutClick}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300">
         
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default UserProfileDialog;
